import * as amplitude from '@amplitude/analytics-browser';

export enum AmplitudeTrackingEvent {
  ConnectedWallet = 'connected_wallet',
  LoginSuccess = 'login_success',
  PageView = 'page_view',
  ClickedFooter = 'clicked_footer',

  PortalCompletedOnboarding = 'portal_completed_onboarding',
  PortalClickedNav = 'portal_clicked_nav',
  PortalAttemptedDailySpin = 'portal_attempted_daily_spin',
  PortalCompletedDailySpin = 'portal_completed_daily_spin',
  PortalCopiedReferralLink = 'portal_copied_referral_link',

  PortalClickedQuest = 'portal_clicked_quest',
  PortalCompletedQuest = 'portal_completed_quest',
  PortalClickedHomeCard = 'portal_clicked_home_card',
  PortalOptimizerDeposit = 'portal_optimizer_deposit',
  PortalOptimizerDepositAttempt = 'portal_optimizer_deposit_attempt',

  PortalOptimizerWithdraw = 'portal_optimizer_withdraw',
  PortalOptimizerWithdrawAttempt = 'portal_optimizer_withdraw_attempt',

  PortalOptimizerRoycoDeposit = 'portal_optimizer_royco_deposit',
  PortalOptimizerRoycoDepositAttempt = 'portal_optimizer_royco_deposit_attempt',
  PortalOptimizerRoycoWithdraw = 'portal_optimizer_royco_withdraw',
  PortalOptimizerRoycoWithdrawAttempt = 'portal_optimizer_royco_withdraw_attempt',

  PortalClickedApp = 'portal_clicked_app',
  PortalConnectedSocials = 'portal_connected_socials',
  PortalCompletedBridge = 'portal_completed_bridge',
  PortalCompletedSwap = 'portal_completed_swap',
  PortalNewsClicked = 'portal_news_clicked',

  PortalClickedKeyLink = 'portal_clicked_key_link',
  PortalClickedBanner = 'portal_clicked_banner',
}

type EventPayloadMap = {
  // Global Events
  [AmplitudeTrackingEvent.ConnectedWallet]: {
    // first_time: boolean;
    wallet_name: string;
    referred_user: boolean;
    plume_product: string;
  };
  [AmplitudeTrackingEvent.LoginSuccess]: {
    // first_time: boolean;
    wallet_name: string;
    referred_user: boolean;
    plume_product: string;
  };
  [AmplitudeTrackingEvent.PageView]: {
    url_path: string;
    plume_product: string;
    utm_source: string | null;
    previous_url_path: string;
  };
  [AmplitudeTrackingEvent.ClickedFooter]: {
    link: string;
  };

  // Portal-specific Events
  [AmplitudeTrackingEvent.PortalClickedNav]: {
    nav_item: string;
  };
  [AmplitudeTrackingEvent.PortalCompletedDailySpin]: {
    spin_streak: number;
    reward_given: string;
    first_time: boolean;
  };
  [AmplitudeTrackingEvent.PortalAttemptedDailySpin]: {
    spin_streak: number;
  };
  [AmplitudeTrackingEvent.PortalClickedQuest]: {
    from: string;
    quest_name: string;
  };
  [AmplitudeTrackingEvent.PortalCompletedQuest]: {
    quest_name: string;
  };
  [AmplitudeTrackingEvent.PortalClickedHomeCard]: {
    card_name: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerDeposit]: {
    vault_name: string;
    deposit_amount: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerDepositAttempt]: {
    vault_name: string;
    deposit_amount: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerRoycoDeposit]: {
    market_name: string;
    deposit_amount: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerRoycoDepositAttempt]: {
    market_name: string;
    deposit_amount: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerWithdraw]: {
    vault_name: string;
    withdraw_amount: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerRoycoWithdraw]: {
    market_name: string;
    withdraw_amount: string;
    claimed_rewards: boolean;
  };
  [AmplitudeTrackingEvent.PortalOptimizerWithdrawAttempt]: {
    vault_name: string;
    withdraw_amount: string;
  };
  [AmplitudeTrackingEvent.PortalOptimizerRoycoWithdrawAttempt]: {
    market_name: string;
    withdraw_amount: string;
    claimed_rewards: boolean;
  };
  [AmplitudeTrackingEvent.PortalClickedApp]: {
    app_name: string;
  };
  [AmplitudeTrackingEvent.PortalConnectedSocials]: {
    social_type: string;
  };
  [AmplitudeTrackingEvent.PortalCompletedBridge]: {
    from_asset_address: string | undefined;
    to_asset_address: string | undefined;
    from_asset: object | undefined;
    to_asset: object | undefined;
    amount_in_formatted: string | undefined;
    tx_hash: string | undefined;
    total_price_impact_usd: string | undefined;
    total_price_impact_percent: string | undefined;
    // amount: string;
    provider: 'enso' | 'relay';
  };
  [AmplitudeTrackingEvent.PortalCompletedSwap]: {
    from_asset_address: string;
    to_asset_address: string;
    tx_hash: string;
    amount_in_formatted: string | undefined;
    amount_out_formatted: string | undefined;
    total_price_impact_percent: string | undefined;
    provider: 'enso' | 'relay';
  };
  [AmplitudeTrackingEvent.PortalNewsClicked]: {
    article_title: string;
  };
  [AmplitudeTrackingEvent.PortalClickedKeyLink]: {
    link_name: string;
  };
  [AmplitudeTrackingEvent.PortalCompletedOnboarding]: Record<string, never>;
  [AmplitudeTrackingEvent.PortalCopiedReferralLink]: Record<string, never>;
  [AmplitudeTrackingEvent.PortalClickedBanner]: {
    banner_name: string;
  };
};

export enum AmplitudeUserProperties {
  // Currently unimplemented properties
  //   FirstTouchPoint = 'first_touch_point',
  //   Region = 'region',
  //   Language = 'language',
  TVLBucket = 'tvl_bucket',
  WalletAddress = 'wallet_address',
  WalletName = 'wallet_name',

  AirdropClaimed = 'airdrop_claimed',
  OnboardingCompleted = 'onboarding_completed',
  QuestsCompleted = 'quests_completed',
  LargestTxnAmount = 'largest_txn_amount',
}

type AmplitudeUserPropertyValue = {
  [AmplitudeUserProperties.WalletAddress]: string | undefined;
  [AmplitudeUserProperties.WalletName]: string | undefined;
  [AmplitudeUserProperties.TVLBucket]: number | undefined;
  [AmplitudeUserProperties.AirdropClaimed]: boolean;
  [AmplitudeUserProperties.OnboardingCompleted]: boolean;
  [AmplitudeUserProperties.QuestsCompleted]: number;
  [AmplitudeUserProperties.LargestTxnAmount]: number;
};

export enum AmplitudeUserGroups {
  Role = 'Role',
}

if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
  amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY);
} else {
  console.warn('NO AMPLITUDE KEY PROVIDED, NO EVENTS WILL BE SENT TO AMPLITUDE');
}

export const trackAmplitudeEvent = <K extends keyof EventPayloadMap>(event: K, properties: EventPayloadMap[K]) => {
  if (import.meta.env.VITE_LOG_EVENT_TRACKING_LOCALLY === 'true') console.log('Tracking event', event, properties);
  if (import.meta.env.VITE_AMPLITUDE_API_KEY) amplitude.track(event, properties);
};

// TODO: possibly extend this to enable operations
// beyond set, there's append, add, postInsert, etc.
// https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#user-properties
export const setAmplitudeUserProperty = <K extends AmplitudeUserProperties, V extends AmplitudeUserPropertyValue[K]>(
  property: K,
  value: V,
) => {
  if (import.meta.env.VITE_LOG_EVENT_TRACKING_LOCALLY === 'true') {
    console.log('Identify Property', property, value);
  }
  if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
    const identifyEvent = new amplitude.Identify();
    if (value) {
      identifyEvent.set(property, value);
      amplitude.identify(identifyEvent);
    }
  }
};

export const setAmplitudeUserId = (userId: string | undefined) => amplitude.setUserId(userId);

export const setAmplitudeUserGroup = (groupName: string, groupValue: string) =>
  amplitude.setGroup(groupName, groupValue);

export const resetAmplitudeTracking = () => amplitude.reset();
